import {PlatformIntl} from 'src/infra/PlatformIntl';
import DataTable from 'src/components/dataTable/index.jsx';
import type {CompanyMissingField, CompanyRow} from './rows';
import {useDeleteCompany} from './mutations';
import useAlerts from 'src/hooks/use-alerts';
import {useIntl} from 'react-intl';
import {useCompanyConfigDispatch} from './hooks';
import {useCompany} from 'src/providers/company';

interface CompanyListViewProps {
  loading: boolean;
  companies: CompanyRow[];
}

const MISSING_FIELD_LABELS: Record<CompanyMissingField, string> = {
  logoUrl: 'companyLogo',
  androidAppUrl: 'companyAndroidAppUrl',
  iosAppUrl: 'companyIOSAppUrl',
};

const CompanyListView = ({loading, companies}: CompanyListViewProps) => {
  const intl = PlatformIntl.getInstance();
  const intlMessage = useIntl();
  const {toast} = useAlerts();
  const dispatch = useCompanyConfigDispatch();
  const currentCompany = useCompany();
  const {mutate: deleteCompany, isPending: isDeleting} = useDeleteCompany();

  // A customer row with no configuration has nothing to remove, and DataTable cannot disable the
  // button per row, so the early return has to say what happened.
  const handleRemove = (company: CompanyRow) => {
    if (!company.id) {
      toast.warn(intlMessage.formatMessage({id: 'companyNothingToRemove'}));
      return;
    }

    deleteCompany(company.id, {
      onSuccess: () => {
        toast.success(intlMessage.formatMessage({id: 'sharedRemovedSuccess'}));
      },
      onError: (error) => {
        const apiError = error as {data?: string; status?: number};
        toast.errorStatus({data: apiError.data, status: apiError.status ?? 500});
      },
    });
  };

  const handleAdd = () => {
    dispatch({type: 'SET_ACTIVE_VIEW', payload: {view: 'create'}});
  };

  const handleEdit = (company: CompanyRow) => {
    dispatch({type: 'SET_ACTIVE_VIEW', payload: {view: 'edit', company}});
  };

  const handleMyCompanyForm = () => {
    dispatch({type: 'SET_ACTIVE_VIEW', payload: {view: 'my_company', company: currentCompany}});
  };

  // DataTable formats every column with the same formatter, so the pending text ships ready on
  // the row.
  const describeMissing = (missing: CompanyMissingField[]) => {
    if (missing.length === 0) return intl.formatMessage('companyComplete');

    const fields = missing.map((field) => intl.formatMessage(MISSING_FIELD_LABELS[field]));
    return `${intl.formatMessage('companyMissing')}: ${fields.join(', ')}`;
  };

  const rows = companies.map((company) => ({
    ...company,
    missingLabel: describeMissing(company.missing),
    displayName: company.customerName || company.customDomain || company.label || '',
  }));

  return (
    <DataTable
      add={handleAdd}
      edit={handleEdit}
      remove={handleRemove}
      list={rows}
      myCompanyForm={handleMyCompanyForm}
      loading={loading || isDeleting}
      keyField={'rowKey'}
      confirmText={`${intl.formatMessage('sharedRemoveMessage')} <strong><field>displayName</field></strong>! </br>${intl.formatMessage('sharedRemoveConfirmationMessage')} `}
      headers={[
        {title: 'sharedCompany', field: 'customerName', dataSort: true},
        {title: 'companyCustomDomain', field: 'customDomain', dataSort: true},
        {title: 'companyLabel', field: 'label', dataSort: true},
        {title: 'companyLogo', field: 'logoUrl', dataSort: true},
        {title: 'companyAndroidAppUrl', field: 'androidAppUrl', dataSort: true},
        {title: 'companyIOSAppUrl', field: 'iosAppUrl', dataSort: true},
        {title: 'companyTheme', field: 'theme', dataSort: true},
        {title: 'companyMissing', field: 'missingLabel', dataSort: true},
      ]}
      excelConfig={{
        fileName: 'sharedCompanies',
        columns: [
          {label: 'sharedCompany', field: 'customerName'},
          {label: 'companyCustomDomain', field: 'customDomain'},
          {label: 'companyLabel', field: 'label'},
          {label: 'companyLogo', field: 'logoUrl'},
          {label: 'companyAndroidAppUrl', field: 'androidAppUrl'},
          {label: 'companyIOSAppUrl', field: 'iosAppUrl'},
          {label: 'companyTheme', field: 'theme'},
          {label: 'companyMissing', field: 'missingLabel'},
        ],
      }}
    />
  );
};

export default CompanyListView;
